import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, StatusBadge, inr } from "@/components/bits";
import api, { errMsg, fmtDate } from "@/lib/api";

const FILTERS = ["pending_review", "resubmitted", "published", "correction_required", "on_hold", "rejected", "closed"];

export default function AdminJobs() {
  const [status, setStatus] = useState("pending_review");
  const [jobs, setJobs] = useState(null);
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const reqId = useRef(0);

  const load = () => {
    const id = ++reqId.current;
    setJobs(null);
    api.get(`/admin/jobs?status=${status}`)
      .then(({ data }) => { if (id === reqId.current) setJobs(data.jobs); })
      .catch(() => { if (id === reqId.current) setJobs([]); });
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const review = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/jobs/${action.job.id}/review`, { action: action.action, reason });
      toast.success(`Job ${action.action}d`);
      setAction(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-jobs-page">
      <div className="flex gap-2 flex-wrap mb-6">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setStatus(f)}
            data-testid={`filter-${f}`}
            className={`text-xs font-semibold rounded-full px-4 py-2 border transition-colors capitalize ${
              status === f ? "bg-[#0B1B3D] text-white border-[#0B1B3D]" : "bg-white border-border hover:border-[#2563EB]"
            }`}
          >
            {f.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      {!jobs ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : jobs.length === 0 ? (
        <EmptyState title={`No jobs in "${status.replace(/_/g, " ")}"`} description="Jobs submitted by employers appear here for review." testId="admin-jobs-empty" />
      ) : (
        <div className="space-y-4">
          {jobs.map((j) => (
            <div key={j.id} className="bg-white border border-border/60 rounded-xl p-6" data-testid={`admin-job-${j.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-heading font-semibold text-lg text-[#0B1B3D]">{j.title}</p>
                    <StatusBadge status={j.status} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {j.company_name} · {j.location} · {j.experience_min}–{j.experience_max} yrs · {inr(j.salary_min)}–{inr(j.salary_max)} ·{" "}
                    <span className="capitalize">{(j.hiring_preference || "own").replace(/_/g, " ")}</span>
                    {j.recruitment_payment_model !== "none" && <span className="capitalize"> · {j.recruitment_payment_model}</span>} · {fmtDate(j.created_at)}
                  </p>
                  {j.description && <p className="text-sm text-muted-foreground mt-3 line-clamp-2 max-w-3xl">{j.description}</p>}
                  <div className="flex gap-1.5 flex-wrap mt-3">
                    {(j.skills || []).slice(0, 6).map((s) => (
                      <span key={s} className="text-[11px] bg-[#2563EB]/8 text-[#2563EB] rounded-full px-2 py-0.5">{s}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-5 flex-wrap">
                {["pending_review", "resubmitted", "on_hold", "correction_required"].includes(j.status) && (
                  <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => setAction({ job: j, action: "approve" })} data-testid={`approve-${j.id}`}>
                    Approve & Publish
                  </Button>
                )}
                {["pending_review", "resubmitted", "published", "on_hold"].includes(j.status) && (
                  <>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAction({ job: j, action: "correction" })} data-testid={`correction-${j.id}`}>
                      Request Correction
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full text-orange-700 border-orange-200" onClick={() => setAction({ job: j, action: "hold" })} data-testid={`hold-${j.id}`}>
                      Hold
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => setAction({ job: j, action: "reject" })} data-testid={`reject-${j.id}`}>
                      Reject
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!action} onOpenChange={() => setAction(null)}>
        <DialogContent aria-describedby={undefined} data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">{action?.action} — {action?.job.title}</DialogTitle>
          </DialogHeader>
          <div>
            <Label>
              {action?.action === "correction" ? "Correction message (sent to employer)" :
               action?.action === "reject" ? "Rejection reason" :
               action?.action === "hold" ? "Hold reason" : "Note (optional)"}
            </Label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1.5"
              placeholder={action?.action === "correction" ? "e.g. Please update salary range and minimum experience." : ""}
              data-testid="review-reason-input"
            />
          </div>
          <Button onClick={review} disabled={busy || (action?.action !== "approve" && !reason.trim())} className="w-full rounded-full bg-[#0B1B3D]" data-testid="review-confirm-btn">
            {busy ? "Processing…" : "Confirm"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
